import { useCallback, useMemo } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { Category } from "../../../../../api/types/accessTypes";
import ContactCommunicationMatrix from "./ContactCommunicationMatrix";

interface Props {
  contactId: string;
  categories: Category[];
  onUpdateContactDetails: () => void;
}

const ContactCommunicationMatrixTab = ({ contactId, categories, onUpdateContactDetails }: Props) => {
  const fetchContactDetails = useCallback(() => adminApi.getContactDetails(contactId), [contactId]);
  const [contactDetails, contactDetailsFetchError] = useFetch(fetchContactDetails);

  const investorIds = useMemo(
    () => distinct((contactDetails?.permissions ?? []).map((p) => p.investorId)),
    [contactDetails]
  );

  const fetchInvestors = useCallback(
    () => adminApi.searchInvestors({ fieldIds: [], includeContacts: false, includeFunds: true, investorIds }),
    [investorIds]
  );
  const [investorsResp, investorsFetchError, { isFetching: isFetchingInvestors }] = useFetch(fetchInvestors, {
    disabled: contactDetails === undefined || investorIds.length === 0,
  });

  if (contactDetailsFetchError) {
    logError(contactDetailsFetchError, "[ContactCommunicationMatrixTab] getContactDetails");
    return <DataLoadingFailed />;
  }

  if (investorsFetchError) {
    logError(investorsFetchError, "[ContactCommunicationMatrixTab] searchInvestors");
    return <DataLoadingFailed />;
  }

  if (contactDetails === undefined || isFetchingInvestors || (investorIds.length > 0 && investorsResp === undefined)) {
    return <InlineLoader />;
  }

  return (
    <ContactCommunicationMatrix
      categories={categories}
      contactDetails={contactDetails}
      investors={investorsResp?.items ?? []}
      onUpdateContactDetails={onUpdateContactDetails}
    />
  );
};

export default ContactCommunicationMatrixTab;
